import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Search, Users, BookOpen, UserPlus, UserMinus, User } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";

const DiscoverWritersPage = () => {
  const { user, api } = useAuth();
  const [writers, setWriters] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [followingStatus, setFollowingStatus] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [writersRes, genresRes] = await Promise.all([
          api.get('/discover/writers', {
            params: {
              search: searchQuery || undefined,
              genre: selectedGenre || undefined
            }
          }),
          api.get('/genres')
        ]);
        setWriters(writersRes.data);
        setGenres(genresRes.data);

        // If logged in, check follow status
        if (user) {
          const followingRes = await api.get(`/users/${user.id}/following`);
          const followingIds = {};
          followingRes.data.forEach(f => { followingIds[f.id] = true; });
          setFollowingStatus(followingIds);
        }
      } catch (error) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, user, searchQuery, selectedGenre]);

  const handleFollow = async (writerId) => {
    if (!user) return;
    
    try {
      if (followingStatus[writerId]) {
        await api.delete(`/users/${writerId}/follow`);
        setFollowingStatus({ ...followingStatus, [writerId]: false });
        setWriters(writers.map(w => 
          w.id === writerId ? { ...w, follower_count: w.follower_count - 1 } : w
        ));
      } else {
        await api.post(`/users/${writerId}/follow`);
        setFollowingStatus({ ...followingStatus, [writerId]: true });
        setWriters(writers.map(w => 
          w.id === writerId ? { ...w, follower_count: w.follower_count + 1 } : w
        ));
      }
    } catch (error) {
      // Non-critical error suppressed
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled by useEffect
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="discover-writers-page">
      {/* Header */}
      <div className="relative mb-12">
        <div className="absolute inset-0 rounded-2xl overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac"
            alt="Community"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/90 to-brand-primary/70" />
        </div>
        <div className="relative p-8 md:p-12">
          <div className="text-white">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-6 h-6 text-brand-gold" />
              <span className="overline text-brand-gold">Find Your Tribe</span>
            </div>
            <h1 className="font-serif text-4xl font-semibold mb-4">Discover Writers</h1>
            <p className="text-white/80 max-w-lg">
              Connect with fellow writers who share your passion. Follow their work, 
              exchange feedback, and grow together.
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              type="text"
              placeholder="Search by name, username, or bio..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="writer-search"
            />
          </div>
        </form>
        
        <Select value={selectedGenre || "all"} onValueChange={(v) => setSelectedGenre(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 h-12" data-testid="genre-filter">
            <SelectValue placeholder="All Genres" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Genres</SelectItem>
            {genres.map((genre) => (
              <SelectItem key={genre} value={genre}>{genre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Writers Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      ) : writers.length === 0 ? (
        <div className="text-center py-20">
          <Users className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">No writers found</h2>
          <p className="text-text-secondary">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {writers.filter(w => w.id !== user?.id).map((writer) => (
            <Card 
              key={writer.id} 
              className="bg-white border-brand-primary/10 card-hover"
              data-testid={`writer-card-${writer.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Link to={`/writer/${writer.id}`}>
                    <Avatar className="w-16 h-16 border-2 border-brand-primary/10">
                      <AvatarImage src={writer.avatar_url} alt={writer.display_name} />
                      <AvatarFallback className="bg-brand-primary text-white text-xl">
                        {(writer.display_name || writer.username)?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/writer/${writer.id}`}>
                      <h3 className="font-serif text-lg font-semibold text-text-primary hover:text-brand-accent transition-colors">
                        {writer.display_name || writer.username}
                      </h3>
                      <p className="text-sm text-text-muted">@{writer.username}</p>
                    </Link>
                  </div>
                </div>

                {writer.bio && (
                  <p className="text-text-secondary text-sm line-clamp-2 mt-4">
                    {writer.bio}
                  </p>
                )}

                {writer.favorite_genres && writer.favorite_genres.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {writer.favorite_genres.slice(0, 3).map(genre => (
                      <span 
                        key={genre}
                        className="text-xs px-2 py-1 rounded-full bg-brand-primary/10 text-brand-primary"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-brand-primary/10">
                  <div className="flex items-center gap-4 text-sm text-text-muted">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {writer.story_count} stories
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {writer.follower_count} followers
                    </span>
                  </div>
                  
                  {user && (
                    <Button
                      variant={followingStatus[writer.id] ? "outline" : "default"}
                      size="sm"
                      onClick={() => handleFollow(writer.id)}
                      className={followingStatus[writer.id] ? "btn-outline" : "btn-primary"}
                      data-testid={`follow-btn-${writer.id}`}
                    >
                      {followingStatus[writer.id] ? (
                        <>
                          <UserMinus className="w-4 h-4 mr-1" />
                          Unfollow
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-1" />
                          Follow
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DiscoverWritersPage;
